/* External */
import {
  memo, useEffect, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import Title from '@dtvgo/title';

/* Assets */
import Icon from '~/assets/theme/svg/icon-fail-circle.svg';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Utils */
import { APP_VERSION, DATADOG_APPLICATION_ID } from '../../../../utils/env';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';
import { getGtmPlatform } from '../../../../utils/googleTags/utils';
import platform from '../../../../utils/platform';

/* Components */
import FocusableButtonProfile from '../../../../components/FocusableButtonProfile';

/* GTM */
import {
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  GTM_PLATFORMS_ERROR_CODES,
} from '../../../../utils/googleTags';

/* Style */
import './TermsOfServiceErrorPage.scss';
import { getLink, Routes } from '../../../../utils/router';

function TermsOfServiceErrorPage({ realFocusKey, setFocus }) {
  const location = useLocation();
  const history = useHistory();
  const country = useCountry();

  const timeElapsed = Date.now();
  const today = new Date(timeElapsed);
  const todayFormat = today.toISOString();

  const pathToRedirect = location?.state?.pathToRedirect;

  const sourceError = location?.state?.source ?? '';
  const typeError = location?.state?.type;
  const externCode = location?.state?.externCode;
  const formattedExternCode = externCode ? ` (${externCode})` : '';

  // TODO: remove this and use new CodeError package when refactoring is done
  const codeErrorMessage = `M10-4099${formattedExternCode}`;

  const gtmPlatform = getGtmPlatform(platform?.aemPlatform);
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);

  const errorTermsCommonTagEvent = useCallback(({ subCategory, eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.TERMS_OF_SERVICE_DATA,
        subCategory: subCategory || GTM_SUBCATEGORIES.PAGES.TERMS_OF_SERVICE_PAGE,
        eventName,
        data: {
          errorType: typeError || '-',
          errorCode: GTM_PLATFORMS_ERROR_CODES[gtmPlatform],
        },
      });
    }
  }, [commonTaggingEvent, typeError, gtmPlatform]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      errorTermsCommonTagEvent({
        eventName: GTM_EVENT_NAMES.TERMS_OF_SERVICE.PAGE_VIEW_ERROR,
      });
      tagsMountRef.current = false;
    }
  }, [errorTermsCommonTagEvent, country]);

  const generalErrorButtonFocusKey = `${realFocusKey}-general-error-button`;

  useEffect(() => {
    setFocus(generalErrorButtonFocusKey);
  }, [generalErrorButtonFocusKey, setFocus]);

  const handleClickButton = useCallback(() => {
    errorTermsCommonTagEvent({
      eventName: GTM_EVENT_NAMES.TERMS_OF_SERVICE.CONTINUE_ERROR_TERMS,
    });

    if (pathToRedirect.toString().includes('player')) {
      history.replace(getLink(Routes.Private.AcceptTermsOfService));
    } else {
      history.replace(getLink(Routes.Private.Profiles));
    }
  }, [errorTermsCommonTagEvent, history, pathToRedirect]);

  return (
    <div className="dtv-html5-c-general-errors">
      <div className="dtv-html5-c-general-errors--center">
        <div className="dtv-html5-c-general-errors--limit">
          <img src={getJsonImage('icon-checkmark-circle') || Icon} alt="dtv Terms Of Service Error" className="dtv-html5-c-general-errors--terms_error" />
          <Title
            title={<FormattedMessage id="termsOfService.error.title" />}
            description={<FormattedMessage id="termsOfService.error.subtitle" />}
            descriptionExists
          />
          <div className="dtv-html5-c-general-errors--moreDetails">
            <div>
              <p className="dtv-html5-c-general-errors--code-error">{codeErrorMessage}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="dtv-html5-c-general-errors--botton">
        <div className="dtv-html5-c-general-errors--buttonAlignmentTerms">
          <FocusableButtonProfile
            type="submit"
            secondGray
            bordered
            focused
            focusKey={generalErrorButtonFocusKey}
            onEnterPress={handleClickButton}
          >
            <FormattedMessage id="termsOfService.error.ok" />
          </FocusableButtonProfile>
        </div>
        <div className="dtv-html5-c-general-errors--info">
          <p>MV010000114200000000000</p>
          <p>{DATADOG_APPLICATION_ID}</p>
          {sourceError === 'MIDDLEWARE' ? (<p>MIDDLEWARE</p>) : ''}
          <p>{todayFormat}</p>
          <p>{APP_VERSION}</p>
        </div>
      </div>
    </div>
  );
}

TermsOfServiceErrorPage.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(TermsOfServiceErrorPage)));
